import { Document } from '@prismicio/client/types/documents'
import type { GetStaticPaths, NextPage } from 'next'
import Head from 'next/head'
import { RichText } from 'prismic-reactjs'
import {
  ArticleBody,
  ArticleContents,
  ArticleWrapper,
  BackToChallenge,
  CompletionWidget,
  Feedback,
  HeroPartner,
  ResourceLink,
  ResourceLinks,
  TopicLinks
} from '../../components/articles'
import { Tag } from '../../components/common'
import { PinButton, Sentiment, UpNext } from '../../components/content'
import { ExternalIcon } from '../../components/icons'
import { registerContent } from '../../lib/name'
import prismic, { defaultOptions, DocumentType } from '../../lib/prismic'
import { useAnalyticsPageView } from '../../lib/state'

interface Props {
  document: Document<any>
  previewRef: any
}

const PartnerResource: NextPage<Props> = ({ document, previewRef }) => {
  const { data, id, type } = document
  const title = RichText.asText(data.title)
  const topicIds = data.topics.map(({ topic }) => topic?.id)
  const contributor = RichText.asText(data.contributor?.data?.name)
  const duration = data.consumption_time
  registerContent(id, { title, contributor, duration, type })
  useAnalyticsPageView({ title, type: 'Partner Resource', content: title, content_author: contributor, topic_ids: topicIds })
  const allLinks: ResourceLink[] = [
    ...data.files.map(file => ({
      label: RichText.asText(file.file_label),
      url: file.file.url,
      type: 'download'
    }))
    ,
    ...data.links.map(link => ({
      label: RichText.asText(link.link_label),
      url: link.link.url,
      type: 'external'
    }))
  ]
    .filter(link => link.url)

  const externalDomain = (new URL(data.external_offer_link.url))

  return (
    <ArticleWrapper contentId={id} previewRef={previewRef} topicIds={topicIds}>
      <Head>
        <title>ACE Compass : {title}</title>
      </Head>
      <BackToChallenge contentId={id} type={DocumentType.PartnerResource} />
      <CompletionWidget format={'mobile'} contentId={id} topicIds={topicIds} />
      <article className='article'>
        <HeroPartner
          title={title}
          externalUrl={data.external_offer_link.url}
          imageUrl={data.hero_image.url}
          imageAlt={data.hero_image.alt}
          partnerLogoUrl={data.partner_logo.url}
          partnerLogoAlt={data.partner_logo.alt}
          partnerDescription={data.partner_description} />
        <section className='article-container'>
          <div className='article-main'>
            <aside className='article-rail'>
              {data.sponsored && (
                <div className='article-rail__sponsored'>
                  <Tag type='sponsored' content='Sponsored' />
                </div>
              )}
              <TopicLinks prismicTopics={data.topics} classNames='rail-widget' />
              <CompletionWidget contentId={id} topicIds={topicIds}>
                Finished reading?
              </CompletionWidget>
            </aside>
            <ArticleContents>
              <ArticleBody>
                <TopicLinks prismicTopics={data.topics} classNames='article-body-topic-widget' />
                <div className='article-resources'>
                  <ResourceLinks links={allLinks} />
                </div>
                <RichText render={data.body} />
                <a className='article-button article-button--blue' href={data.external_offer_link.url} target="_blank" rel="noreferrer">
                  <span className='article-button__text'>
                    view this partner resource at {externalDomain.hostname} <ExternalIcon />
                  </span>
                </a>
                <PinButton className='article-body__pin-button' contentId={id} topicIds={topicIds} />
              </ArticleBody>
            </ArticleContents>
          </div>
        </section>
        <Feedback>
          <Sentiment contentId={id} topicIds={topicIds} />
        </Feedback>
      </article>
      <UpNext contentId={id} />
    </ArticleWrapper>
  )
}

export default PartnerResource

export const getStaticPaths: GetStaticPaths = async () => {
  const { results } = await prismic.queryByType(DocumentType.PartnerResource, { pageSize: 100 })
  const paths = results.map(({ uid }) => ({
    params: { uid },
  }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params, previewData }) {
  const previewRef = previewData ? previewData.ref : null
  const refOption = previewRef ? { ref: previewRef } : null
  const document = await prismic.getByUID(DocumentType.PartnerResource, params.uid, { ...defaultOptions, ...refOption })

  return {
    props: {
      previewRef,
      document
    },
    revalidate: 60 * 60 // 1 hour
  }
}
